<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { loadTurnstile } from "@composables/turnstile";

// Cloudflare Turnstile anti-spam widget. Emits "token" with the token once
// the visitor passes, and "" whenever it expires or is reset. Tokens work
// once, so the parent calls reset() after every submission.
const props = defineProps({
  siteKey: {
    type: String,
    required: true,
  },
  // Checked on the server, so a token can't be reused on another form.
  action: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["token"]);

const container = ref(null);
const failed = ref(false);
let widgetId = null;
let unmounted = false;

onMounted(async () => {
  try {
    const turnstile = await loadTurnstile();
    if (unmounted) {
      return;
    }
    widgetId = turnstile.render(container.value, {
      sitekey: props.siteKey,
      action: props.action,
      // The forms are light; "auto" would follow the visitor's dark mode.
      theme: "light",
      // Match the width of the fields above rather than a fixed 300px.
      size: "flexible",
      callback: (token) => emit("token", token),
      "expired-callback": () => emit("token", ""),
      "error-callback": () => emit("token", ""),
    });
  } catch (error) {
    console.error(error);
    failed.value = true;
  }
});

onBeforeUnmount(() => {
  unmounted = true;
  if (widgetId !== null && window.turnstile) {
    window.turnstile.remove(widgetId);
  }
});

const reset = () => {
  emit("token", "");
  if (widgetId !== null && window.turnstile) {
    window.turnstile.reset(widgetId);
  }
};

defineExpose({ reset });
</script>

<template>
  <div>
    <div ref="container"></div>
    <p v-if="failed" class="text-14 text-error">
      The security check couldn't load. If you use an ad or content blocker,
      please allow this site, then refresh the page.
    </p>
  </div>
</template>
